import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ExternalLink, Star, Trash2, Upload } from "lucide-react";
import { api, errText } from "@/lib/api";
import { uploadFile } from "@/admin/fields";

const SLUG = "james";

const Check = ({ checked, onChange, label, testid }) => (
  <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="w-5 h-5 accent-[#C9A24B]" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} /> {label}</label>
);

const Photos = ({ photos, portrait, busy, onUpload, onMove, onDelete, onPrivacy, onPortrait }) => (
  <div data-testid="james-photos">
    <div className="flex flex-wrap items-center gap-3">
      <p className="label !mb-0">My Pictures (only photos you upload here are shown)</p>
      <label className="btn-ghost btn-sm cursor-pointer ml-auto"><Upload size={14} /> {busy ? "Uploading..." : "Upload photos"}<input type="file" multiple accept="image/*" hidden onChange={(e) => onUpload([...e.target.files])} data-testid="james-photo-upload" /></label>
    </div>
    {!photos.length && <p className="mt-3 text-sm text-[#F6EFE0]/75" data-testid="james-photos-empty">No pictures yet. James's page shows an empty frame with the monogram J until you upload one.</p>}
    <div className="mt-4 grid gap-4 grid-cols-2 md:grid-cols-4">
      {photos.map((m, i) => (
        <div key={m.id} className="rounded-xl border border-[#C9A24B]/25 overflow-hidden" data-testid={`james-photo-${i}`}>
          <img src={m.url} alt="" className="w-full aspect-square object-cover" />
          <div className="p-2 grid gap-2">
            <select className="field !py-1.5 text-xs" value={m.privacy === "hidden" ? "hidden" : "family"} onChange={(e) => onPrivacy(m, e.target.value)} data-testid={`james-photo-privacy-${i}`}>
              <option value="family">Shown on James's page</option><option value="hidden">Hidden (admin only)</option>
            </select>
            <div className="flex gap-1">
              <button className="lb-btn !w-8 !h-8" disabled={i === 0} onClick={() => onMove(i, -1)} aria-label="Move up" data-testid={`james-photo-up-${i}`}><ArrowUp size={14} /></button>
              <button className="lb-btn !w-8 !h-8" disabled={i === photos.length - 1} onClick={() => onMove(i, 1)} aria-label="Move down" data-testid={`james-photo-down-${i}`}><ArrowDown size={14} /></button>
              <button className={`lb-btn !w-8 !h-8 ${portrait === m.id ? "!bg-[#C9A24B] !text-[#0B1D14]" : ""}`} onClick={() => onPortrait(m.id)} aria-label="Use as portrait" title="Use as portrait" data-testid={`james-photo-portrait-${i}`}><Star size={14} /></button>
              <button className="lb-btn !w-8 !h-8 ml-auto" onClick={() => onDelete(m)} aria-label="Delete" data-testid={`james-photo-delete-${i}`}><Trash2 size={14} /></button>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const JamesTab = () => {
  const [p, setP] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const loadPhotos = () => api.get(`/admin/personal/${SLUG}/photos`).then((r) => setPhotos(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => {
    api.get("/admin/personal").then((r) => setP((r.data || []).find((x) => x.slug === SLUG) || null)).catch(() => {});
    loadPhotos();
  }, []);
  if (!p) return <p className="text-sm text-[#F6EFE0]/75" data-testid="admin-james-tab">James's page is not set up yet.</p>;
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const setRow = (i, k, v) => set("details", p.details.map((d, j) => (j === i ? { ...d, [k]: v } : d)));
  const setCh = (i, k, v) => set("chapters", p.chapters.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const access = (key, on) => set("also_open_to", on ? [...new Set([...(p.also_open_to || []), key])] : (p.also_open_to || []).filter((x) => x !== key));
  const put = async (body) => { const r = await api.put(`/admin/personal/${SLUG}`, body); return r.data; };
  const save = async () => {
    try { const { enabled, birth_date, details, chapters, also_open_to, privacy_note } = p; await put({ enabled, birth_date, details, chapters, also_open_to, privacy_note }); toast.success("James's page saved"); } catch (e) { toast.error(errText(e)); }
  };
  const saveGallery = async (ids, extra = {}) => { await put({ gallery: ids, ...extra }); set("gallery", ids); await loadPhotos(); };
  const onUpload = async (files) => {
    if (!files.length) return;
    setBusy(true);
    try {
      const ids = [...(p.gallery || [])];
      for (const f of files) { const m = await uploadFile(f, { privacy: "family", in_gallery: "false" }); ids.push(m.id); }
      await saveGallery(ids, p.portrait ? {} : { portrait: ids[0] });
      if (!p.portrait) set("portrait", ids[0]);
      toast.success("Uploaded");
    } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  const onMove = async (i, d) => { const ids = photos.map((m) => m.id); [ids[i], ids[i + d]] = [ids[i + d], ids[i]]; try { await saveGallery(ids); } catch (e) { toast.error(errText(e)); } };
  const onDelete = async (m) => {
    if (!window.confirm("Delete this picture from James's page?")) return;
    try {
      const ids = photos.map((x) => x.id).filter((x) => x !== m.id);
      const portrait = p.portrait === m.id ? ids[0] || null : p.portrait;
      await saveGallery(ids, { portrait }); set("portrait", portrait);
      await api.delete(`/admin/media/${m.id}`);
      toast.success("Picture deleted");
    } catch (e) { toast.error(errText(e)); }
  };
  const onPrivacy = async (m, v) => { try { await api.put(`/admin/media/${m.id}`, { privacy: v }); await loadPhotos(); } catch (e) { toast.error(errText(e)); } };
  const onPortrait = async (id) => { try { await put({ portrait: id }); set("portrait", id); toast.success("Portrait set"); } catch (e) { toast.error(errText(e)); } };
  return (
    <div data-testid="admin-james-tab">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h2 className="font-display text-3xl mr-4">James's Page</h2>
        <a href={`/personal/${SLUG}`} target="_blank" rel="noreferrer" className="btn-ghost btn-sm ml-auto" data-testid="james-view-page"><ExternalLink size={14} /> View page</a>
      </div>
      <div className="grid gap-6">
        <div className="flex flex-wrap gap-6">
          <Check checked={p.enabled} onChange={(v) => set("enabled", v)} label="Page switched on" testid="james-enabled" />
          <Check checked={(p.also_open_to || []).includes("family")} onChange={(v) => access("family", v)} label="Also open with the family code" testid="james-open-family" />
          <Check checked={(p.also_open_to || []).includes("personal:johan")} onChange={(v) => access("personal:johan", v)} label="Also open with Johan's code" testid="james-open-johan" />
        </div>
        <div><label className="label">Privacy note shown under his name (leave empty to hide)</label><input className="field" value={p.privacy_note || ""} onChange={(e) => set("privacy_note", e.target.value)} data-testid="james-privacy-note" /></div>
        <div className="max-w-xs"><label className="label">Birth date (for automatic age)</label><input type="date" className="field" value={p.birth_date || ""} onChange={(e) => set("birth_date", e.target.value)} data-testid="james-birth-date" /></div>
        <div>
          <p className="label">Details card (each field has its own show/hide switch; use __AGE__ for the automatic age)</p>
          <div className="grid gap-2">
            {(p.details || []).map((d, i) => (
              <div key={i} className="grid gap-2 grid-cols-[1fr] sm:grid-cols-[180px_1fr_auto] items-center" data-testid={`james-detail-${i}`}>
                <input className="field" value={d.label} onChange={(e) => setRow(i, "label", e.target.value)} aria-label="Label" data-testid={`james-detail-label-${i}`} />
                <input className="field" value={d.value} onChange={(e) => setRow(i, "value", e.target.value)} aria-label="Value" data-testid={`james-detail-value-${i}`} />
                <Check checked={d.visible !== false} onChange={(v) => setRow(i, "visible", v)} label="Show" testid={`james-detail-visible-${i}`} />
              </div>
            ))}
          </div>
        </div>
        <p className="label !mb-0">Chapters</p>
        {(p.chapters || []).map((c, i) => (
          <details key={c.key} className="rounded-xl border border-[#C9A24B]/25 p-4" data-testid={`james-chapter-${c.key}`}>
            <summary className="flex items-center gap-3 cursor-pointer">
              <span className="flex-1 font-display text-lg">{c.title}</span>
              <span onClick={(e) => e.stopPropagation()}><Check checked={c.visible !== false} onChange={(v) => setCh(i, "visible", v)} label="Show" testid={`james-chapter-visible-${c.key}`} /></span>
            </summary>
            <div className="mt-4 grid gap-3">
              <input className="field" value={c.title} onChange={(e) => setCh(i, "title", e.target.value)} />
              {c.key !== "pictures" && <textarea className="field min-h-[160px]" placeholder="Leave empty to show Coming soon" value={(c.paragraphs || []).join("\n\n")} onChange={(e) => setCh(i, "paragraphs", e.target.value.split(/\n\s*\n/).filter((x) => x.trim()))} data-testid={`james-chapter-text-${c.key}`} />}
            </div>
          </details>
        ))}
        <button className="btn-gold w-fit" onClick={save} data-testid="james-save-btn">Save James's page</button>
        <Photos photos={photos} portrait={p.portrait} busy={busy} onUpload={onUpload} onMove={onMove} onDelete={onDelete} onPrivacy={onPrivacy} onPortrait={onPortrait} />
      </div>
    </div>
  );
};
